<template>
  <div class="space-y-3">
    <div class="mt-3 text-2xl font-medium text-gray-500">
      <h1>Abrir Ticket</h1>
    </div>

    <ExternalClientInfo
      :client="client"
      :loading="loading"
      v-if="!loadingError"
    />

    <div
      v-if="!loadingError && orderCod && !isLead"
      class="bg-white border border-gray-200 rounded shadow-sm"
    >
      <div
        id="order-number"
        v-if="!loading"
        class="px-4 py-3 text-base font-medium"
      >
        Número do Pedido: {{ orderCod }}
      </div>

      <div
        v-show="loading"
      >
        <ExternalLoading
          py="0"
          text="Carregando informações do pedido"
        />
      </div>
    </div>

    <div
      v-if="!loadingError && orderItems.length && !isLead"
      class="space-y-3 bg-white border border-gray-200 rounded shadow-sm"
    >
      <div
        v-if="!loading"
        class="p-4 space-y-3"
      >
        <span
          class="text-base font-medium"
        >
          Qual produto necessita de atendimento?
        </span>

        <div class="flex flex-row justify-start items-center space-x-3">
          <input
            id="select-all-items"
            v-model="allItemsSelected"
            type="checkbox"
            class="w-5 h-5 sm:w-4 sm:h-4 text-orange-organization focus:ring-0 active:ring-0 rounded-sm border-gray-500 disabled:border-gray-200 disabled:bg-gray-100"
            @change="checkSelected()"
          />
          <label
            for="select-all-items"
            class="text-gray-800 leading-5"
          >
            {{ allItemsSelected ? 'Desmarcar' : 'Marcar' }} todos os itens
          </label>
        </div>
      </div>

      <div
        class="h-40 px-4 overflow-auto overscroll-auto max-h-96 scrollbar-thin scrollbar-thumb-rounded-full scrollbar-thumb-gray-400 scrollbar-track-gray-200"
        id="order-items-list"
        v-if="!loading && !loadingError"
      >
        <ExternalOrderItemInfo
          v-for="(orderItem, key) in orderItems"
          :key="key"
          :order-item="orderItem"
          :selected-items-ids="selectedItemsIds"
          :show-checkbox="true"
          :organization="organization"
        />
      </div>

      <div
        v-show="loading"
        class="p-4"
      >
        <ExternalLoading
          py="0"
          text="Carregando informações dos items"
        />
      </div>
    </div>

    <div
      class="p-4 space-y-3 bg-white border border-gray-200 rounded shadow-sm"
    >
      <div v-if="!loading && !loadingError">
        <div>
          <form
            @submit.prevent="createTicket"
            class="space-y-4"
          >
            <div
              class="mt-1 text-base font-medium"
            >
              O que aconteceu?*
            </div>

            <vSelect
              v-model="selectedWorkflow"
              :options="getSanitizedWorkflows"
              label="title"
              code="_id"
              class=""
            />

            <ExternalLoading
              v-if="sending"
              py="0"
            />

            <div
              class="flex flex-row-reverse w-full"
            >
              <button
                type="submit"
                class="flex items-center justify-center w-3/12 h-8 px-2 py-1 font-medium text-white transition-opacity duration-300 border border-transparent rounded focus:outline-none focus:ring-0 disabled:opacity-50 bg-orange-organization hover:opacity-80 disabled:cursor-not-allowed"
                :disabled="sending || snackBar.show || !selectedWorkflow || wrongWorkflow"
              >
                Abrir Ticket
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-show="loading && !loadingError">
        <ExternalLoading
          py="0"
          text="Carregando workflows"
        />
      </div>

      <div v-if="loadingError">
        <div class="flex justify-center w-full">
          <button
            class="flex items-center justify-center w-16 h-16 text-white bg-indigo-800 rounded-full"
            @click="init()">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <ExternalSnackbar
      :show="snackBar.show"
      @closed="snackBar.show = false"
    >
      {{ snackBar.message }}
    </ExternalSnackbar>
  </div>
</template>

<script>
import ExternalClientInfo from './commons/ExternalClientInfo'
import ExternalOrderItemInfo from './commons/ExternalOrderItemInfo'
import ExternalSnackbar from '@/components/commons/ExternalSnackbar'
import ExternalLoading from '@/components/commons/ExternalLoading'
import { create as createTicket, findTicketTasks } from '@/repositories/integrations/mobly/services/csExternal/ticket'
import { findByMatch as workflowFind } from '@/repositories/integrations/mobly/services/csExternal/workflow'
import {
  findOne as findOneCustomer,
  findByPathAndDocument,
  create as CustomerCreate
} from '@/repositories/integrations/mobly/services/csExternal/customer'
import {
  findOne as findOneLead
} from '@/repositories/integrations/mobly/services/csExternal/lead'
import { findOne as findOneUser } from '@/repositories/integrations/mobly/services/csExternal/user'
import { find as findInternalConfig } from '@/repositories/integrations/mobly/services/csExternal/internalConfig'
import { find as findOrderItems } from '@/repositories/integrations/mobly/services/csExternal/orderItem'
import { find as findStatus } from '@/repositories/integrations/mobly/services/csExternal/status'
import { findOne as findOneOrder } from '@/repositories/integrations/mobly/services/csExternal/order'
import { findOne as findOneOrderOs } from '@/repositories/integrations/mobly/services/csExternal/csOs'
import { encrypt } from '@/repositories/integrations/mobly/services/csExternal/encrypt'
import { ORDER, ORDER_ITEM } from '@/enum/enum.workflow-types'
import { MOBLY } from '@/utils/possible-organizations-values'

export default {
  name: 'ExternalCreateTicket',

  props: {
    clientDocument: String,
    orderCod: String,
    orderItemCod: String,
    organization: String,
    workflowId: {
      type: String,
      required: false
    },
    hash: String,
    type: String
  },

  components: {
    ExternalClientInfo,
    ExternalOrderItemInfo,
    ExternalSnackbar,
    ExternalLoading
  },

  data () {
    return {
      loading: true,
      selectedWorkflow: '',
      sending: false,
      sla: null,
      workflowList: [],
      workflowType: 'customer',
      userId: null,
      user: {},
      client: {},
      order: {},
      orderItems: [],
      form: {},
      defaultForm: {
        emailTemplate: '',
        emails: [{
          to: '',
          cc: '',
          cco: '',
          subject: '',
          message: '',
          sendToClient: false
        }]
      },
      snackBar: {
        show: false,
        message: ''
      },
      loadingError: false,
      allItemsSelected: false,
      selectedItemsIds: [],
      wrongWorkflow: false,
      isLead: false
    }
  },

  computed: {
    getSanitizedWorkflows () {
      return this.workflowList.map(workflow => ({
        _id: workflow._id,
        title: workflow.title,
        types: workflow.types,
        multipleItems: workflow.multipleItems,
        singleItem: workflow.singleItem,
        toLowerCase: () => workflow.title.toLowerCase(),
        toString: () => workflow.title
      }))
    }
  },

  watch: {
    selectedWorkflow () {
      if (!this.selectedWorkflow || typeof this.selectedWorkflow !== 'object') {
        delete this.form.workflow
        this.wrongWorkflow = true
        this.selectedWorkflow = ''
        return
      }

      this.wrongWorkflow = false

      const multipleItems = this.selectedWorkflow.multipleItems
      const singleItem = this.selectedWorkflow.singleItem

      if (this.selectedItemsIds.length === 1 && multipleItems && !singleItem) {
        this.snackBar.message = `Para utilizar um Workflow com a tag [Múltiplo]
        é necessário selecionar ao menos 2 items do pedido.`
        this.snackBar.show = true
        this.wrongWorkflow = true
        return
      }

      if (this.selectedItemsIds.length > 1 && !multipleItems && singleItem) {
        this.snackBar.message = `Para utilizar um Workflow com a tag [Único]
        é necessário selecionar apenas 1 item do pedido.`
        this.snackBar.show = true
        this.wrongWorkflow = true
        return
      }

      this.form.workflow = this.selectedWorkflow._id
    },

    selectedItemsIds () {
      this.allItemsSelected = this.orderItems.length === this.selectedItemsIds.length
      this.changeWorkflowTypeFilter()
      this.getWorkflowList()
    }
  },

  mounted () {
    this.init()
  },

  methods: {
    async init () {
      try {
        this.loading = true
        this.loadingError = false
        this.isLead = this.type === 'lead'

        if (this.organization === MOBLY && !this.isLead) {
          try {
            const statusCustomerResponse = await findByPathAndDocument(this.clientDocument, this.organization)
            const statusCustomer = statusCustomerResponse.data
            const { status } = statusCustomer

            if (!status) {
              this.$router.push('/notFound')
              return
            }

            if (status === 'blocked' || status === 'inactive') {
              this.$router.push('/blocked')
              return
            }
          } catch (e) {
            console.error(e)
          }
        }
        this.form = JSON.parse(JSON.stringify(this.defaultForm))

        // Get Client
        const clientParams = `fields=_id,name,email,document&hash=${this.hash}`
        if (this.isLead) {
          const leadResponse = await findOneLead(
            this.clientDocument, clientParams, this.organization
          )
          if (leadResponse.data) {
            this.client = leadResponse.data
          } else {
            throw new Error('Lead not found')
          }
        } else {
          const clientResponse = await findOneCustomer(
            this.clientDocument, clientParams, this.organization
          )
          this.client = clientResponse.data ? clientResponse.data : await this.findAndCreateCustomerFromClientService(this.clientDocument)
        }

        // Get UserConfig
        const configParams = 'cod=mobly-external-user'
        const configResponse = await findInternalConfig(configParams, this.organization)
        this.userId = configResponse.data.docs[0].config._id

        // Get User
        const userParams = ''
        const userResponse = await findOneUser(this.userId, userParams, this.organization)
        this.user = userResponse.data

        // Get Order and OrderItems
        if (this.orderCod && !this.isLead) {
          let orderResponse = null

          try {
            const orderParams = 'fields=_id,customer_id'
            orderResponse = await findOneOrder(this.orderCod, orderParams, this.organization)
          } catch (e) {
            if (e.response.status === 404) {
              orderResponse = await findOneOrderOs(this.orderCod, this.organization)
            }
          }

          if (orderResponse.data.customer_id === this.client._id) {
            this.order = orderResponse.data
            let itemsParams = `orderCode=${this.orderCod}`
            itemsParams += '&fields=docs(_id,cod,name,sku,order,shipping,statuses)'
            const orderItemsResponse = await findOrderItems(itemsParams, this.organization)
            this.orderItems = orderItemsResponse.data.docs
          }
        }

        // Select OrderItem
        if (this.orderItemCod) {
          const selectedItem = this.orderItems.find(
            item => item.cod === this.orderItemCod
          )
          if (selectedItem) this.selectedItemsIds.push(selectedItem._id)
        }

        // Get Workflows
        this.changeWorkflowTypeFilter()
        await this.getWorkflowList()

        // Select Workflow
        if (this.workflowId) {
          this.setWorkflow()
        }
      } catch (err) {
        if (err.response.status === 401) {
          window.location.href = '/unauthorized'
        }

        this.loadingError = true
        this.showSnackBar(err)
      }

      this.loading = false
    },

    changeWorkflowTypeFilter () {
      delete this.form.workflow
      this.selectedWorkflow = ''

      if (this.isLead) {
        this.workflowType = 'lead'
        return
      }

      const selectedOrderItemsLength = this.selectedItemsIds.length

      if (!selectedOrderItemsLength) {
        this.workflowType = 'customer'
        this.showOrderItems = true
        return
      }

      if (!this.allItemsSelected) {
        this.workflowType = 'orderItem'
        this.showOrderItems = true
        return
      }

      if (selectedOrderItemsLength === 1) {
        this.workflowType = 'order,orderItem'
        this.showOrderItems = true
        return
      }

      this.workflowType = 'order'
      this.showOrderItems = true
    },

    async getWorkflowList () {
      this.workflowList = []

      const { data } = await workflowFind(this.makeWorkflowUrl(), this.organization)

      this.workflowList = data
    },

    makeWorkflowUrl (page = 1) {
      let params = `types=${this.workflowType}&isPublic=true&page=${page}&isVisible=true`

      if (this.workflowType === 'order,orderItem') {
        params += `&orderItems=${this.selectedItemsIds.join(',')}&order=${this.order._id}`

        return params
      }

      if (this.workflowType === 'orderItem') {
        params += `&orderItems=${this.selectedItemsIds.join(',')}`

        return params
      }

      if (this.workflowType === 'order') {
        params += `&order=${this.order._id}`

        return params
      }

      if (this.workflowType === 'customer') {
        params += `&customer=${this.client._id}`

        return params
      }

      if (this.workflowType === 'lead') {
        params += `&lead=${this.client._id}`

        return params
      }
    },

    async createTicket () {
      this.sending = true

      try {
        const formattedTicket = await this.formatTicket(this.form)

        const { data: { _id: ticketId } } = await createTicket(formattedTicket, this.organization)

        if (!ticketId) return

        const params = 'sort=created_at&limit=100&doneAt=null'
        const { data: { docs: ticketTasks } } = await findTicketTasks(
          ticketId, params, this.organization
        )

        // Se não tiver nenhum ticketTask em aberto retorna
        if (!ticketTasks.length) {
          this.$router.push('/ticketCreated')
          return
        }

        const userGroups = this.user.groups.map(group => group.group._id)

        let autopopTask = null

        const toDoStatuses = await findStatus('types=to_do&fields=_id', this.organization)
        const toDoStatusesIds = toDoStatuses.data.map(status => status._id)

        ticketTasks.forEach(ticketTask => {
          if (autopopTask) return
          ticketTask.tasks.forEach(task => {
            if (autopopTask) return
            if (
              task.auto_pop &&
              userGroups.includes(task.group) &&
              toDoStatusesIds.includes(task.status[task.status.length - 1])
            ) {
              task.ticketTaskId = ticketTask._id
              autopopTask = task
            }
          })
        })

        if (!autopopTask) {
          this.$router.push('/ticketCreated')
          return
        }

        const ticketTaskId = autopopTask.ticketTaskId
        const taskId = autopopTask._id

        const hash = await encrypt({
          ticket: ticketId,
          task: taskId,
          ticketTask: ticketTaskId,
          organization: this.organization,
          type: this.type
        }, this.organization)

        const encodedHash = encodeURIComponent(hash.data.hash)

        this.$router.push(`/taskDetail?h=${encodedHash}&org=${this.organization}`)
      } catch (err) {
        this.showSnackBar(err.response.data.message)
      }
    },

    formatTicket (form) {
      const selectedWorkflow = this.workflowList.find(
        workflow => workflow._id === form.workflow
      )

      form.type = this.getWorkflowType(this.workflowType)

      form.title = selectedWorkflow.title
      form.description = selectedWorkflow.description
      form.sla = selectedWorkflow.sla
      form.user = this.userId
      if (this.isLead) {
        form.lead = this.client._id
      } else {
        form.customer = this.client._id
      }
      form.workflow = selectedWorkflow._id
      form.emails[0].to = this.client.email
      form.emails[0].subject = selectedWorkflow.title
      form.emails[0].message = 'Seu ticket foi criado com sucesso :)'

      if (form.type === ORDER.key ||
        form.type === ORDER_ITEM.key
      ) {
        form.order = this.order._id
        form.orderItem = this.selectedItemsIds
      }

      return form
    },

    getWorkflowType (type) {
      if (type !== 'order,orderItem') return type

      if (this.selectedWorkflow.types.length === 1) return this.selectedWorkflow.types[0]

      return 'orderItem'
    },

    checkSelected () {
      this.selectedItemsIds = []

      if (!this.allItemsSelected) return

      this.selectedItemsIds = this.orderItems.map(item => item._id)
    },

    showSnackBar (message) {
      this.sending = false
      this.snackBar.message = message
      this.snackBar.show = true
      this.snackBar.icon = true
      this.snackBar.closeMessage = ''
    },

    async getAllWorkflows (data) {
      const { pages } = data
      let { page } = data

      const listOfPages = []

      for (let index = 0; index < pages; ++index, ++page) {
        listOfPages[index] = page
      }

      const listOfWorkflows = await Promise.all(listOfPages.map(
        page => workflowFind(this.makeWorkflowUrl(page), this.organization)
      ))

      return this.formatPaginateWorkflows(listOfWorkflows)
    },

    formatPaginateWorkflows (listOfWorkflows) {
      const options = []

      listOfWorkflows.forEach(workflowsPages => {
        options.push(...workflowsPages.data.docs)
      })

      return options
    },

    getWorkflowTag (workflow) {
      if (workflow.multipleItems && workflow.singleItem) {
        return 'Único/Múltiplo'
      }

      if (workflow.multipleItems && !workflow.singleItem) {
        return 'Múltiplo'
      }

      return 'Único'
    },

    setWorkflow () {
      if (!this.workflowId) return
      const lgpd = this.workflowList.find((item) => item._id === this.workflowId)
      if (lgpd === undefined) return
      this.selectedWorkflow = {
        multipleItems: lgpd.multipleItems,
        singleItem: lgpd.singleItem,
        title: lgpd.title,
        types: lgpd.types,
        _id: lgpd._id,
        toLowerCase: () => lgpd.title.toLowerCase(),
        toString: () => lgpd.title
      }
    },

    async findAndCreateCustomerFromClientService (document) {
      const { data } = await findByPathAndDocument(document, this.organization, this.hash)
      if (!data) return
      await CustomerCreate(data)
      const clientResponse = await findOneCustomer(data.document, `fields=_id,name,email,document&hash=${this.hash}`, this.organization)
      return clientResponse.data
    }
  }
}
</script>
